<!-- Input controls (INPUT_BOX / SLIDER): a dashboard input that feeds a
     formula/measure value into one or more target charts. -->
<div class="wf-section" *ngIf="active">
  <header class="wf-section-header">
    <h3>{{ host.layoutSectionTitle('input', isSliderLayout ? 'Slider input' : 'Input box') }}</h3>
  </header>
  <small class="control-hint">{{ host.layoutSectionHint('input', 'This control feeds its value into the target chart(s) selected below.') }}</small>

  <div class="control-group control-group-full" *ngIf="host.fieldEnabled('input', 'targetCharts')">
    <label>{{ host.fieldLabel('input', 'targetCharts', 'Target chart(s) / input(s)') }}</label>
    <ng-select
      #ssInputTargets
      class="ss-select ss-select--multi ss-select--full"
      [class.is-open]="!!ssInputTargets.isOpen()"
      appendTo="body"
      ngClass="ss-dropdown-panel"
      [items]="host.targetCharts"
      bindLabel="label"
      bindValue="value"
      [multiple]="true"
      [closeOnSelect]="false"
      [hideSelected]="false"
      [clearable]="true"
      [searchable]="true"
      placeholder="Select the chart(s) or input(s) this control drives"
      [(ngModel)]="host.selectedTargetCharts"
    ></ng-select>
    <small class="control-hint" *ngIf="!host.targetCharts.length">Add charts or other inputs to the dashboard first — they'll appear here as targets.</small>
  </div>
  <ng-container *ngTemplateOutlet="apiCallConfigBlock; context: { kind: 'input' }"></ng-container>

  <!-- Per-target formula: each selected target gets its own expression fed by
       this control's `value` (e.g. KPI: [price] * value; another input:
       value * 2). A WATERFALL target instead lists one formula per bridge
       step, since each bar is independently calculated. Each row is a drop
       zone — drag a column from the Data pane to add its token. -->
  <div class="control-group control-group-full" *ngIf="host.selectedTargetCharts.length">
    <label>{{ host.fieldLabel('input', 'formula', 'Formula per target') }}</label>
    <ng-container *ngFor="let id of host.selectedTargetCharts">
      <div class="target-formula-group" *ngIf="host.isWaterfallTarget(id)">
        <span class="target-formula-name" [title]="host.targetLabelForId(id)">{{ host.targetLabelForId(id) }} — per bar</span>
        <div class="target-formula-bar" *ngFor="let step of host.targetSteps(id); trackBy: host.trackByStepLabel">
          <div
            class="target-formula-row wf-field-drop-zone"
            (dragover)="host.allowFieldDrop($event)"
            (drop)="host.onTargetFormulaDrop($event, id, step.label)"
            title="Drag a column here to add it to this bar's formula"
          >
            <span class="target-formula-name" [title]="step.label">{{ step.label || step.stepType }}</span>
            <input
              type="text"
              [ngModel]="host.targetStepFormula(id, step.label)"
              (ngModelChange)="host.setTargetStepFormula(id, step.label, $event)"
              placeholder="e.g. [price] * value"
            />
          </div>

          <!-- This bar's own references — same [Token] mechanism as below,
               scoped to THIS bar's formula only. -->
          <ng-container *ngIf="host.formulaReferencesEnabledFor('WATERFALL')">
            <ng-container
              *ngTemplateOutlet="referenceList; context: { id: id, step: step.label }"
            ></ng-container>
          </ng-container>
        </div>
      </div>
      <div *ngIf="!host.isWaterfallTarget(id)">
        <div
          class="target-formula-row wf-field-drop-zone"
          (dragover)="host.allowFieldDrop($event)"
          (drop)="host.onTargetFormulaDrop($event, id)"
          title="Drag a column here to add it to this target's formula"
        >
          <span class="target-formula-name" [title]="host.targetLabelForId(id)">{{ host.targetLabelForId(id) }}</span>
          <input
            type="text"
            [ngModel]="host.targetFormulas[id] || ''"
            (ngModelChange)="host.targetFormulas[id] = $event"
            [placeholder]="host.isInputControlTargetId(id) ? 'value (default — passes this control’s value through)' : 'e.g. [price] * value'"
          />
        </div>
        <!-- A CHART target with no formula is silently skipped by the
             recompute (nothing to calculate) — surface that instead. -->
        <small class="control-warning" *ngIf="!host.isInputControlTargetId(id) && !(host.targetFormulas[id] || '').trim()">
          No formula yet — "{{ host.targetLabelForId(id) }}" will NOT update when this control changes. Add one, e.g. [{{ host.targetLabelForId(id) }}]*(1+(value/100))
        </small>

        <!-- References: when a formula's [Token] isn't a plain dataset
             column but another input's live/recomputed output (e.g. a row
             inside a different target chart), type in where it actually
             comes from so the backend resolves the CURRENT value instead
             of a stale one. Fully typed by hand — no drag-and-drop. -->
        <ng-container *ngIf="host.formulaReferencesEnabledFor(host.targetLayoutTypeForId(id))">
          <ng-container
            *ngTemplateOutlet="referenceList; context: { id: id }"
          ></ng-container>
        </ng-container>
      </div>
    </ng-container>
  </div>

  <div class="control-row" *ngIf="isSliderLayout && (host.fieldEnabled('input', 'min') || host.fieldEnabled('input', 'max') || host.fieldEnabled('input', 'step'))">
    <div class="control-group" *ngIf="host.fieldEnabled('input', 'min')">
      <label>{{ host.fieldLabel('input', 'min', 'Min') }}</label>
      <input type="number" [(ngModel)]="sliderMin" />
    </div>
    <div class="control-group" *ngIf="host.fieldEnabled('input', 'max')">
      <label>{{ host.fieldLabel('input', 'max', 'Max') }}</label>
      <input type="number" [(ngModel)]="sliderMax" />
    </div>
    <div class="control-group" *ngIf="host.fieldEnabled('input', 'step')">
      <label>{{ host.fieldLabel('input', 'step', 'Step') }}</label>
      <input type="number" [(ngModel)]="sliderStep" />
    </div>
  </div>

  <div class="control-group control-group-full" *ngIf="host.fieldEnabled('input', 'defaultValue')">
    <label>{{ host.fieldLabel('input', 'defaultValue', 'Default value') }}</label>
    <input
      type="number"
      [(ngModel)]="inputDefaultValue"
      [placeholder]="isSliderLayout ? 'Handle start position (optional)' : 'Pre-filled value (optional)'"
    />
  </div>
  <!-- No Format accordion here by design: Slider / Input Box tiles don't
       take card-header formatting (builder or published). -->
</div>
